/**
 * ListeDetay — bir posta listesinin içi (backlog #585): adı ve notu, üyeleri (sayfalı, aranabilir), üye çıkarma ve ekleme.
 *
 * Her personel okur; adı, notu ve üyeleri yalnızca listeyi oluşturan ya da Sistem Ayarları yetkisi olan değiştirir (liste satırındaki `can_edit`; backend 403 verir).
 * Üyeler kullanıcı kimlikleridir: adres posta giderken kullanıcıdan okunur. Adresi olmayan üye listede kırmızı "adres yok" ile görünür, pasif kullanıcı "pasif" ile
 * (postayı yine alır). Üye eklemek iki yoldan: ad / e-posta ile arayıp tek tek, ya da Kullanıcılar süzgeciyle toplu (ListeOlustur, ekleme kipinde).
 *
 * `isOpen` kapalıyken hiçbir şey çizilmez: her açılışta form sıfırdan başlar.
 */
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import ListeOlustur from "./ListeOlustur";
import { searchSendUsers } from "../../../../../services/emailAyarService";
import { addMailListMembers, getMailList, listMailListMembers, removeMailListMembers, updateMailList } from "../../../../../services/postaListeleriService";

const SAYFA = 15;

const DetayForm = ({ liste, toggle, onChanged }) => {
    const { t } = useTranslation("moduller");
    const [kayit, setKayit] = useState(liste);
    const [ad, setAd] = useState(liste.mail_list_name || "");
    const [not, setNot] = useState(liste.mail_list_note || "");
    const [sayfa, setSayfa] = useState(1);
    const [arama, setArama] = useState("");
    const [uygulananArama, setUygulananArama] = useState("");
    const [uyeler, setUyeler] = useState({ data: [], total: 0, totalPages: 1 });
    const [yukleniyor, setYukleniyor] = useState(true);
    const [yenile, setYenile] = useState(0);
    const [hata, setHata] = useState(null);
    const [bilgi, setBilgi] = useState(null);
    const [kaydediyor, setKaydediyor] = useState(false);
    const [sorgu, setSorgu] = useState("");
    const [bulunan, setBulunan] = useState(null);
    const [suzgecAcik, setSuzgecAcik] = useState(false);
    const duzenleyebilir = Boolean(kayit.can_edit);
    const id = liste.mail_list_id;

    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const [d, u] = await Promise.all([getMailList(id), listMailListMembers(id, { page: sayfa, limit: SAYFA, search: uygulananArama })]);
                if (ignore) return;
                setKayit(d);
                setUyeler(u);
                setHata(null);
            } catch (e) {
                if (!ignore) setHata(e.message);
            } finally {
                if (!ignore) setYukleniyor(false);
            }
        }
        run();
        return () => { ignore = true; };
    }, [id, sayfa, uygulananArama, yenile]);

    function degisti() {
        setYenile((n) => n + 1);
        if (onChanged) onChanged();
    }

    async function adiKaydet() {
        setKaydediyor(true);
        setHata(null);
        setBilgi(null);
        try {
            await updateMailList(id, { name: ad.trim(), note: not.trim() });
            setBilgi(t("emailsListe.kaydedildi"));
            degisti();
        } catch (e) {
            setHata(e.message);
        } finally {
            setKaydediyor(false);
        }
    }

    async function cikar(userId) {
        setHata(null);
        setBilgi(null);
        try {
            await removeMailListMembers(id, [userId]);
            degisti();
        } catch (e) {
            setHata(e.message);
        }
    }

    async function ara() {
        if (sorgu.trim().length < 2) return;
        setHata(null);
        try {
            setBulunan(await searchSendUsers(sorgu.trim()));
        } catch (e) {
            setHata(e.message);
        }
    }

    async function ekle(userId) {
        setHata(null);
        setBilgi(null);
        try {
            await addMailListMembers(id, { userIds: [userId] });
            degisti();
        } catch (e) {
            setHata(e.message);
        }
    }

    return (
        <>
            <Modal isOpen toggle={toggle} size="xl" scrollable>
                <ModalHeader toggle={toggle}>{kayit.mail_list_name}</ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-2">
                        {t("emailsListe.uyeSayisi", { n: kayit.member_count ?? 0 })}
                        {kayit.without_address > 0 && <span className="text-danger"> · {t("emailsListe.adresiYok", { n: kayit.without_address })}</span>}
                        {kayit.inactive > 0 && <span> · {t("emailsListe.pasifSayisi", { n: kayit.inactive })}</span>}
                        {kayit.creator_name && <span> · {t("emailsListe.olusturan", { ad: kayit.creator_name })}</span>}
                    </p>

                    {duzenleyebilir ? (
                        <div className="row g-2 align-items-end mb-3">
                            <div className="col-md-4">
                                <Label className="form-label" htmlFor="detay-ad">{t("emailsListe.listeAdi")}</Label>
                                <Input id="detay-ad" value={ad} maxLength={150} onChange={(e) => setAd(e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <Label className="form-label" htmlFor="detay-not">{t("emailsListe.not")}</Label>
                                <Input id="detay-not" value={not} maxLength={500} onChange={(e) => setNot(e.target.value)} />
                            </div>
                            <div className="col-md-2">
                                <Button color="primary" outline className="w-100" disabled={kaydediyor || ad.trim() === ""} onClick={adiKaydet}>
                                    {kaydediyor ? <Spinner size="sm" /> : t("emailsListe.kaydet")}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Alert color="light" className="py-2">{t("emailsListe.salt")}{kayit.mail_list_note ? ` — ${kayit.mail_list_note}` : ""}</Alert>
                    )}

                    {hata && <Alert color="danger">{hata}</Alert>}
                    {bilgi && <Alert color="success" className="py-2">{bilgi}</Alert>}

                    {duzenleyebilir && (
                        <div className="border rounded p-3 mb-3">
                            <div className="d-flex flex-wrap gap-2 align-items-end">
                                <div className="flex-grow-1">
                                    <Label className="form-label" htmlFor="detay-kullanici-ara">{t("emailsListe.uyeAra")}</Label>
                                    <Input
                                        id="detay-kullanici-ara" type="search" value={sorgu} placeholder={t("emailsListe.kullaniciAraYeri")}
                                        onChange={(e) => setSorgu(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ara(); } }}
                                    />
                                </div>
                                <Button color="primary" outline disabled={sorgu.trim().length < 2} onClick={ara}>{t("emailsListe.ara")}</Button>
                                <Button color="primary" onClick={() => setSuzgecAcik(true)}>{t("emailsListe.suzgecleEkle")}</Button>
                            </div>
                            {bulunan && bulunan.length === 0 && <div className="form-text">{t("emailsListe.kullaniciYok")}</div>}
                            {bulunan && bulunan.length > 0 && (
                                <ul className="list-unstyled mt-2 mb-0">
                                    {bulunan.map((u) => (
                                        <li key={u.user_id} className="d-flex align-items-center justify-content-between py-1">
                                            <span>{u.user_name} {u.user_surname} <span className="text-muted">({u.user_email || "—"})</span></span>
                                            <Button size="sm" color="light" onClick={() => ekle(u.user_id)}>{t("emailsListe.ekle")}</Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <div className="d-flex gap-2 mb-2">
                        <Input
                            id="detay-uye-ara" type="search" value={arama} placeholder={t("emailsListe.uyelerdeAra")}
                            onChange={(e) => setArama(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setSayfa(1); setUygulananArama(arama.trim()); } }}
                        />
                        <Button color="light" onClick={() => { setSayfa(1); setUygulananArama(arama.trim()); }}>{t("emailsListe.ara")}</Button>
                    </div>
                    {yukleniyor ? <div className="text-center py-3"><Spinner size="sm" /></div> : (
                        <Table size="sm" responsive className="align-middle">
                            <thead>
                                <tr>
                                    <th>{t("emailsListe.kullanici")}</th>
                                    <th>{t("emailsListe.eposta")}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {uyeler.data.length === 0 && <tr><td colSpan={3} className="text-muted">{t("emailsListe.uyeYok")}</td></tr>}
                                {uyeler.data.map((u) => (
                                    <tr key={u.user_id}>
                                        <td>{u.user_name} {u.user_surname} <span className="text-muted small">{u.user_login_name}</span>{!u.user_active && <Badge color="secondary" className="ms-2">{t("emailsListe.pasif")}</Badge>}</td>
                                        <td>{u.user_email || <span className="text-danger">{t("emailsListe.adresYok")}</span>}</td>
                                        <td className="text-end">
                                            {duzenleyebilir && <Button size="sm" color="danger" outline onClick={() => cikar(u.user_id)}>{t("emailsListe.cikar")}</Button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted small">{t("emailsListe.sayfa", { sayfa, toplam: uyeler.totalPages })}</span>
                        <div className="d-flex gap-2">
                            <Button size="sm" color="light" disabled={sayfa <= 1} onClick={() => setSayfa(sayfa - 1)}>{t("emailsListe.onceki")}</Button>
                            <Button size="sm" color="light" disabled={sayfa >= uyeler.totalPages} onClick={() => setSayfa(sayfa + 1)}>{t("emailsListe.sonraki")}</Button>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={toggle}>{t("emailsGonder.kapat")}</Button>
                </ModalFooter>
            </Modal>
            <ListeOlustur
                isOpen={suzgecAcik} liste={kayit} toggle={() => setSuzgecAcik(false)}
                onDone={() => { setSuzgecAcik(false); setBilgi(t("emailsListe.uyelerEklendi")); degisti(); }}
            />
        </>
    );
};

const ListeDetay = ({ isOpen, liste, toggle, onChanged }) => (isOpen && liste ? <DetayForm liste={liste} toggle={toggle} onChanged={onChanged} /> : null);

export default ListeDetay;
